<script setup lang="ts">
import {
  ECompassDirection,
  EControlEventName,
  EPresetKeyboardAction,
  type IControlEffectCallback,
  type IControlStateInfo,
  type IPresetKeyboardInteractiveBlock,
  type IPresetKeyboardMap,
  type TEditControlInteractive,
} from '@/utils/cloud-device-sdk'

import { EControlBtnUseType } from '@/types/components'

import SVG_CLICK_ICON from '@/assets/svg/game-control-gui/click.svg?raw'
import SVG_ROTATE_ICON from '@/assets/svg/game-control-gui/rotate.svg?raw'
import SVG_ZOOM_ICON from '@/assets/svg/game-control-gui/zoom.svg?raw'
import SVG_COMPASS_ICON from '@/assets/svg/game-control-gui/compass.svg?raw'
import SVG_MOUSE_ICON from '@/assets/svg/game-control-gui/mouse.svg'
import SVG_ARROW_ICON from '@/assets/svg/game-control-gui/arrow.svg?raw'

const props = defineProps<{
  controlEffect: IControlEffectCallback
  mouseX: number
  mouseY: number
  mouseInGameArea: boolean
  isHorizontal: boolean
  getStatusInfo: () => IControlStateInfo
  addControlArea: (interactive: IPresetKeyboardInteractiveBlock) => void
  setControlArea: (interactive: TEditControlInteractive, id: string) => void
  deleteControlArea: (id: string) => void
  showAddBtnPanel: { x: number, y: number } | Record<string, never>
}>()

const emits = defineEmits(['hideAddPanel', 'freezeControl'])

const customKeyboardDataInject = inject<{
  isInTipsPresetKeyboardStatus: globalThis.Ref<boolean>
  isInEditPresetKeyboardStatus: globalThis.Ref<boolean>
  currentStatusConfigKeyboardPosition: IPresetKeyboardMap
}>('custom-keyboard-data')

const { t } = useI18n()

let brandText = 'EASYFUN'.split('').map(v => ({ name: v, code: [`Key${v}`] }))

const buttonPosition = ref<IPresetKeyboardMap | undefined>()
const controlPanelRect = ref({
  width: 0,
  height: 0,
  x: 0,
  y: 0,
})
const wRatio = computed(() => (controlPanelRect.value.width / 1000))
const hRatio = computed(() => (controlPanelRect.value.height / 1000))

const hotKeyIdList = ref(new Set())
const keyDownKeyList = new Set<string>()

// add panel btn
const addBtnPanelElement = ref<HTMLElement>()
const addPanelBtnList = computed(() => ([
  {
    name: t(`RTC.presetGameBtnClick`) || 'Press',
    icon: SVG_CLICK_ICON,
    action: EPresetKeyboardAction.TOUCH,
  },
  {
    name: t(`RTC.presetGameBtnArrow`) || 'Compass',
    icon: SVG_ARROW_ICON,
    action: EPresetKeyboardAction.COMPASS,
  },
  {
    name: t(`RTC.presetGameBtnRotate`) || 'Rotate',
    icon: SVG_ROTATE_ICON,
    action: EPresetKeyboardAction.ROTATION,
  },
  {
    name: t(`RTC.presetGameBtnZoom`) || 'Zoom',
    icon: SVG_ZOOM_ICON,
    action: EPresetKeyboardAction.ZOOM,
  },
].map(v => ({ ...v, x: 0, y: 0, w: 60, h: 60, addCount: 0 }))))

const currentInputKeyId = ref('')
const compassBtnNameStyle = reactive({
  [ECompassDirection.UP]: 'left: 50%; top: 0; transform: translateX(-50%);',
  [ECompassDirection.DOWN]: 'left: 50%; bottom: 0; transform: translateX(-50%);',
  [ECompassDirection.LEFT]: 'left: 0; top: 50%; transform: translateY(-50%);',
  [ECompassDirection.RIGHT]: 'right: 0; top: 50%; transform: translateY(-50%);',
})
const compassCurrentEditBtn = ref<ECompassDirection>()
const canAddZoomOrRotate = computed(() => {
  const res = {
    zoom: false,
    rotate: false,
  }

  const keyMap = buttonPosition.value?.interactive || {}
  Object.keys(keyMap).map(values => keyMap[values]).forEach((v) => {
    if (v.type === EPresetKeyboardAction.ZOOM)
      res.zoom = true
    else if (v.type === EPresetKeyboardAction.ROTATION)
      res.rotate = true
  })
  return res
})

// toggle add btn panel
onClickOutside(addBtnPanelElement, () => {
  if (!props.mouseInGameArea)
    return
  emits('hideAddPanel')
  addPanelBtnList.value.forEach(v => v.addCount = 0)
})
const { isOutside: mouseIsOutsideAddBtnPanelElement } = useMouseInElement(addBtnPanelElement)

function draggingAddBtn(event: number[], idx: number) {
  addPanelBtnList.value[idx].x = event[0]
  addPanelBtnList.value[idx].y = event[1]
}

function addBtn(event: number[], idx: number, isClickAdd = false) {
  const area = addPanelBtnList.value[idx]
  addPanelBtnList.value[idx] = { ...area, x: 0, y: 0, addCount: area.addCount += 1 }
  if (!mouseIsOutsideAddBtnPanelElement.value && !isClickAdd)
    return

  let x = props.mouseX
  let y = props.mouseY
  if (isClickAdd) {
    x = props.showAddBtnPanel.x - area.w
    y = props.showAddBtnPanel.y - area.h
  }

  if (area.action === EPresetKeyboardAction.COMPASS) {
    props.addControlArea({
      x,
      y,
      width: 150 * wRatio.value,
      height: 150 * wRatio.value,
      code: ['KeyW', 'KeyA', 'KeyS', 'KeyD'],
      name: {
        [ECompassDirection.UP]: 'W',
        [ECompassDirection.LEFT]: 'A',
        [ECompassDirection.DOWN]: 'S',
        [ECompassDirection.RIGHT]: 'D',
      },
      type: area.action,
      zIndex: 1,
    })
  }
  else {
    if (!brandText.length)
      brandText = 'EASYFUN'.split('').map(v => ({ name: v, code: [`Key${v}`] }))

    const { code, name } = brandText.shift()!
    props.addControlArea({
      x,
      y,
      width: area.w,
      height: area.h,
      code,
      name,
      type: area.action,
      zIndex: 1,
    })
  }
}

function modifyButtonPosition(event: number[], id: string) {
  if (!buttonPosition.value)
    return
  const [x, y] = event
  props.setControlArea({ x, y }, id)
}

function resize(data: Array<number>, id: string) {
  if (!buttonPosition.value)
    return
  const [_, x, y, width, height] = data
  props.setControlArea({ x, y, width, height }, id)
}

function triggerCompassBtnEdit(id: string, direction: ECompassDirection | undefined) {
  currentInputKeyId.value = id
  compassCurrentEditBtn.value = direction
}

// register btn position & set ref render GUI
watchOnce(() => props.getStatusInfo, () => {
  const { configKeyboard } = props.getStatusInfo()
  if (configKeyboard) {
    buttonPosition.value = configKeyboard
    if (customKeyboardDataInject)
      customKeyboardDataInject.currentStatusConfigKeyboardPosition = configKeyboard
  }
}, { immediate: true })

watchOnce(() => props.controlEffect, () => {
  if (props.controlEffect) {
    // update game video area size for calc ratio
    props.controlEffect<Record<'width' | 'height' | 'x' | 'y', number>>(data => controlPanelRect.value = data, [EControlEventName.CONTROL_RECT_RESIZE])

    props.controlEffect<{ uiId: Array<string> }>((data, event) => {
      if (currentInputKeyId.value)
        return
      data.uiId.map((id) => {
        event === EControlEventName.PRESET_KEYBOARD_DOWN ? hotKeyIdList.value.add(id) : hotKeyIdList.value.delete(id)
        return id
      })
    }, [EControlEventName.PRESET_KEYBOARD_DOWN, EControlEventName.PRESET_KEYBOARD_UP], true)

    // add control area key code
    props.controlEffect<{ event: KeyboardEvent }>(({ event }, effectName) => {
      if (!currentInputKeyId.value || !buttonPosition.value)
        return
      event.stopPropagation()
      const keyData = JSON.stringify({ code: event.code, name: event.key })

      if (effectName === EControlEventName.KEYBOARD_UP) {
        keyDownKeyList.delete(keyData)
      }
      else {
        if (keyDownKeyList.size > 2)
          return

        keyDownKeyList.add(keyData)
        const code: string[] = []
        const name: string[] = []
        keyDownKeyList.forEach((val) => {
          code.push(JSON.parse(val).code)
          name.push(JSON.parse(val).name)
        })
        props.setControlArea({ code, name: name.join('+'), direction: compassCurrentEditBtn.value }, currentInputKeyId.value)
      }
    }, [EControlEventName.KEYBOARD_DOWN, EControlEventName.KEYBOARD_UP], true)

    // listen control area data refresh vue GUI
    props.controlEffect<{ data: IPresetKeyboardMap }>(({ data }) => {
      buttonPosition.value = { ...buttonPosition.value, ...data }
      if (customKeyboardDataInject)
        customKeyboardDataInject.currentStatusConfigKeyboardPosition = buttonPosition.value
    }, [EControlEventName.KEYBOARD_DATA_UPDATE])
  }
}, { immediate: true })

watch(() => currentInputKeyId.value, () => {
  emits('freezeControl', !!currentInputKeyId.value)
})
</script>

<template>
  <template v-if="buttonPosition && (customKeyboardDataInject.isInEditPresetKeyboardStatus.value || customKeyboardDataInject.isInTipsPresetKeyboardStatus.value)">
    <GameControlBtn
      v-for="(id) in Object.keys(buttonPosition.interactive)"
      :id="`${buttonPosition.interactive[id].name}-${id}-${controlPanelRect.width}`"
      :key="`${buttonPosition.interactive[id].name}-${id}-${controlPanelRect.width}`"
      :class="{ 'pointer-events-none!': customKeyboardDataInject.isInTipsPresetKeyboardStatus.value }"
      :w="buttonPosition.interactive[id].width * wRatio"
      :h="buttonPosition.interactive[id].height * wRatio"
      :top="buttonPosition.interactive[id].y * hRatio"
      :left="buttonPosition.interactive[id].x * wRatio"
      :parent="true"
      :hot="hotKeyIdList.has(id)"
      :use-type="EControlBtnUseType.EDIT"
      @drag-stop="(e) => modifyButtonPosition(e, id)"
      @resize="(e) => resize(e, id)"
      @delete="() => props.deleteControlArea(id)"
      @dblclick="buttonPosition.interactive[id]?.type !== EPresetKeyboardAction.COMPASS ? (currentInputKeyId = id) : () => { }"
      @blur="buttonPosition.interactive[id].type !== EPresetKeyboardAction.COMPASS ? (currentInputKeyId = '') : triggerCompassBtnEdit('', undefined)"
      @mousedown.stop @mousemove.stop @mouseup.stop @touchstart.stop @touchmove.stop @touchend.stop @touchcancel.stop
    >
      <!-- area attr info -->
      <div
        class="size-full flex flex-col items-center justify-end"
        :style="{ fontSize: `${buttonPosition.interactive[id].width * wRatio}px` }"
      >
        <div
          class="absolute left-50% top-0 size-80% -translate-x-50%"
          :class="{ 'flex-center': buttonPosition.interactive[id].type !== EPresetKeyboardAction.COMPASS }"
          v-html="[SVG_CLICK_ICON, SVG_COMPASS_ICON, SVG_ZOOM_ICON, SVG_ROTATE_ICON][buttonPosition.interactive[id].type]"
        />
        <!-- normal -->
        <template v-if="buttonPosition.interactive[id].type !== EPresetKeyboardAction.COMPASS">
          <span
            class="z-2 mb-0.15em cursor-text p-0.15em text-0.2em c-#fff font-600"
            :class="{ 'bg-#000 bg-op-20 lh-100% rd-4px c-green-5! edit-cursor-animation': id === currentInputKeyId }"
          >{{
            buttonPosition.interactive[id].name }}</span>
        </template>

        <!-- compass four btn -->
        <div v-else class="absolute left-50% top-50% size-full size-60%! -translate-x-50% -translate-y-50%">
          <div class="relative size-full">
            <span
              v-for="(name, direction) in buttonPosition.interactive[id].name" :key="name"
              class="absolute z-2 mb-0.15em cursor-text p-0.15em text-0.1em c-#fff font-600"
              :class="{ 'bg-#000 bg-op-20 rd-4px c-green-5! edit-cursor-animation absolute!': id === currentInputKeyId && compassCurrentEditBtn === direction }"
              :style="compassBtnNameStyle[direction]" @dblclick="triggerCompassBtnEdit(id, direction)"
            >{{ name }}</span>
          </div>
        </div>
      </div>
    </GameControlBtn>
  </template>

  <template v-if="customKeyboardDataInject.isInEditPresetKeyboardStatus.value">
    <!-- cursor tips -->
    <div
      v-if="!Object.keys(showAddBtnPanel).length"
      class="absolute flex-center rd-8px bg-gray-4 p-x-16px p-y-12px font-600" :class="{ hidden: !mouseInGameArea }"
      :style="{ left: `${mouseX + 24}px`, top: `${mouseY + 20}px` }"
    >
      <SVG_MOUSE_ICON />
      <span class="mis-8px whitespace-nowrap font-size-14px c-#fff c-op-90 font-600 lh-1em">{{
        $t('RTC.interactionLayerMouseTips') }}</span>
    </div>

    <!-- add btn panel -->
    <vue-draggable-resizable
      v-else ref="addBtnPanelElement" class="absolute left-0 top-0" w="auto" h="auto"
      :parent="true" :draggable="false" :resizable="false" :x="showAddBtnPanel.x" :y="showAddBtnPanel.y"
    >
      <div class="grid grid-cols-3 grid-gap-16px rd-12px bg-gray-3 p-24px">
        <div
          v-for="(item, idx) in addPanelBtnList" :key="`${idx}-${item.addCount}`"
          class="relative h-80px min-w-60px w-60px flex flex-col items-center justify-end"
        >
          <GameControlBtn
            :w="60" :h="60" :top="item.y" :left="item.x" :parent="false" :hot="false"
            :use-type="EControlBtnUseType.ADD"
            :disabled="item.action === EPresetKeyboardAction.ZOOM ? canAddZoomOrRotate.zoom : (item.action === EPresetKeyboardAction.ROTATION ? canAddZoomOrRotate.rotate : false)"
            :class="{
              'add-btn-fade-in': item.addCount && item.y === 0 && item.x === 0,
              'pointer-events-none! opacity-30': item.action === EPresetKeyboardAction.ZOOM ? canAddZoomOrRotate.zoom : (item.action === EPresetKeyboardAction.ROTATION ? canAddZoomOrRotate.rotate : false),
            }"
            @dragging="(e) => draggingAddBtn(e, idx)"
            @drag-stop="(e) => addBtn(e, idx)"
            @click="addBtn([0], idx, true)"
          >
            <div class="size-full" v-html="item.icon" />
          </GameControlBtn>
          <span class="font-size-12px c-#fff c-op-50 font-600">{{ item.name }}</span>
        </div>
      </div>
    </vue-draggable-resizable>
  </template>
</template>

<style lang="scss" scoped>
.add-btn-fade-in {
  @keyframes fade-in {
    0% {
      opacity: 0;
    }

    80% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  animation: fade-in 500ms ease;
}

.edit-cursor-animation {
  position: relative;

  @keyframes wake {
    0% {
      opacity: 0;
    }

    10% {
      opacity: 1;
    }

    50% {
      opacity: 0;
    }

    99.99% {
      opacity: 0;
    }

    100% {
      opacity: 0;
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 4px;
    width: calc(100% - 8px);
    height: 2px;
    background-color: #ccff00;
    animation: wake 1s linear infinite;
  }
}
</style>
